import React from 'react';
import ReactDOM from 'react-dom';
import { FormGroup } from '@components/molecules/form/FormGroup';
import { TextareaInput } from '@components/atoms/TextareaInput';
import { Button } from '@components/atoms/Button';
import { TaskStatus } from '@models/task.model';
import { Z_INDEX } from '@constants/zIndex';
import { useTranslation } from '@hooks/useTranslation';

interface ConfirmationDialogProps {
  isOpen: boolean;
  title: string;
  message: string;
  statusLabel: string;
  newStatus: string;
  rejectionReason: string;
  onReasonChange: (reason: string) => void;
  onConfirm: () => void;
  onCancel: () => void;
  isLoading: boolean;
  /**
   * Label for the confirm button. Defaults to "Confirm Change". The invoice-pending
   * variant uses "Complete anyway" to make the informational intent obvious.
   */
  confirmLabel?: string;
}

export const ConfirmationDialog: React.FC<ConfirmationDialogProps> = ({
  isOpen,
  title,
  message,
  statusLabel,
  newStatus,
  rejectionReason,
  onReasonChange,
  onConfirm,
  onCancel,
  isLoading,
  confirmLabel,
}) => {
  // Sprint 28, story-144. `taskDetail.*` — docs/architecture.md "## Sprint 28" Decision 1.
  const tTaskDetail = useTranslation('taskDetail');
  const tCommon = useTranslation('common');
  const resolvedConfirmLabel = confirmLabel ?? tTaskDetail.confirmChange;
  if (!isOpen) return null;

  const modalContent = (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center"
      style={{ zIndex: Z_INDEX.EMERGENCY_MODAL }}>
      <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
        <div className="flex items-center mb-4">
          <div className="flex-shrink-0">
            <svg
              className="h-6 w-6 text-yellow-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 18.5c-.77.833.192 2.5 1.732 2.5z"
              />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-lg font-medium text-gray-900">{title}</h3>
          </div>
        </div>

        <div className="mb-6">
          {title === tTaskDetail.completeWithIncompleteSubtasksTitle ? (
            <>
              <p className="text-sm text-gray-600">
                {message}
              </p>
              <p className="text-sm text-red-600 mt-2">
                <strong>{tTaskDetail.warningLabel}</strong> {tTaskDetail.warningIrreversibleContinue}
              </p>
            </>
          ) : title === tTaskDetail.pendingInvoiceItemsTitle ? (
            // Informational variant for the pre-COMPLETE invoice gate — no red warning,
            // just a heads-up that there's still work to invoice. SUPERINTENDENT sees this
            // even though they can't invoice, so they know to tell their manager.
            <p className="text-sm text-gray-700 whitespace-pre-line">{message}</p>
          ) : (
            <>
              <p className="text-sm text-gray-600">
                {tTaskDetail.confirmChangeStatusTo.replace('{status}', statusLabel)}
              </p>
              <p className="text-sm text-red-600 mt-2">
                <strong>{tTaskDetail.warningLabel}</strong> {tTaskDetail.warningIrreversible}
              </p>
            </>
          )}

          {/* Rejection Reason Field */}
          {newStatus === TaskStatus.REJECTED && (
            <div className="mt-4">
              <FormGroup
                htmlFor="rejectionReason"
                label={tTaskDetail.reasonForRejection}>
                <TextareaInput
                  id="rejectionReason"
                  value={rejectionReason}
                  onChange={(e) => onReasonChange(e.target.value)}
                  className="block w-full border border-gray-300 rounded-md p-2 text-sm focus:ring-blue-500 focus:border-blue-500 resize-none h-20"
                  placeholder={tTaskDetail.enterRejectionReason}
                />
              </FormGroup>
            </div>
          )}
        </div>

        <div className="flex justify-end space-x-3">
          <Button onClick={onCancel} disabled={isLoading}>
            {tCommon.cancel}
          </Button>
          <Button onClick={onConfirm} disabled={isLoading}>
            {isLoading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent inline-block mr-2"></div>
                {tTaskDetail.updating}
              </>
            ) : (
              resolvedConfirmLabel
            )}
          </Button>
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modalContent, document.body);
};